/**
 * Tua História — configurador de telas por números (sem app).
 *
 * O cliente carrega uma foto e vê, numa tela 3D, a pintura acabada, o desenho numerado e a
 * foto original. A conversão é feita no browser (é só ilustrativa: o desenho real é preparado
 * pela PrintKK). Ao adicionar ao carrinho, a foto e uma imagem da tela 3D vão para o servidor
 * (POST /tela/foto) e ficam nas propriedades da linha: _tela_foto e _preview.
 *
 * O kit de cada variante vem do SKU: TELA-20X30, TELA-20X30-GRADE, TELA-40X50,
 * TELA-40X50-GRADE, TELA-40X50-CAVALETE (o servidor usa o mesmo SKU para escolher o produto PrintKK).
 */
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.170.0/build/three.module.min.js';

const TEXTOS = {
  pt: {
    titulo: 'Cria a tua tela por números',
    subtitulo: 'Transforma a tua foto numa pintura feita por ti: recebes a tela com o desenho numerado, as tintas certas e os pincéis.',
    uploadBotao: 'Carrega a tua foto',
    uploadArrastar: 'ou arrasta-a para aqui',
    uploadAjuda: 'JPG ou PNG · até 15 MB',
    dica: 'Dica: fotos nítidas, com boa luz e caras grandes dão as pinturas mais bonitas.',
    aPreparar: 'A preparar a tua tela…',
    arrastar: 'Arrasta para rodar',
    mudarImagem: 'Mudar foto',
    vistas: { numeros: 'Por pintar', pintada: 'Pintada' },
    original: 'Ver original',
    tamanho: 'Tamanho',
    acabamento: 'Acabamento',
    acabamentos: { tela: 'Só a tela', grade: 'Com grade de madeira', cavalete: 'Grade + cavalete' },
    acabamentosAjuda: {
      tela: 'Tela enrolada, pronta a pintar em cima da mesa.',
      grade: 'Tela já esticada numa grade de madeira, pronta a pendurar.',
      cavalete: 'Tela com grade e um cavalete de madeira para pintar e expor.',
    },
    adicionar: 'Adicionar ao carrinho', adicionarTotal: 'Adicionar ao carrinho · {total}', suasTelas: 'As tuas telas', telaN: 'Tela {n}', adicionarTela: '+ Adicionar tela', totalTelas: '{n} telas · {total}', difTela: 'Cada tela pode ter a sua foto, tamanho e acabamento. Toca numa tela para a ver.', fotoPrincipal: 'a tua foto', rotFoto: 'Foto', mesmaFoto: 'Mesma foto', outraFoto: 'Outra foto', trocarFoto: 'Trocar foto', adicionarFoto: 'Adicionar foto', remover: 'Remover',
    aAdicionar: 'A adicionar…',
    adicionado: 'Adicionado ao carrinho ✓',
    verCarrinho: 'Ver carrinho',
    outra: 'Criar outra tela',
    semImagem: 'Carrega uma foto para continuar',
    esgotado: 'Esgotado',
    prazo: 'Feita por encomenda · enviada entre {min} a {max} dias',
    politicaEnvio: 'Política de envio',
    privacidade: 'A tua foto é usada só para criar a tua tela e é apagada se não fizeres a encomenda ou assim que a receberes.',
    politicaPrivacidade: 'Política de privacidade',
    kit: 'O teu kit',
    kitInclui: 'Tela {medida} com o desenho numerado · tintas acrílicas numeradas (até 24 cores, escolhidas para a tua foto) · 3 pincéis',
    kitGrade: ' · grade de madeira e ganchos para pendurar',
    kitCavalete: ' · grade de madeira e cavalete',
    cores: 'As tintas da tua foto',
    coresAjuda: '{n} cores escolhidas automaticamente',
    aviso: 'Pré-visualização ilustrativa: o desenho numerado final é preparado a partir da tua foto e pode ter zonas e números diferentes.',
    erroTipo: 'Este ficheiro não é uma imagem. Usa uma foto JPG ou PNG.',
    erroTamanho: 'A foto é maior do que {mb} MB. Escolhe uma foto mais leve.',
    erroLer: 'Não conseguimos abrir esta foto. Experimenta outra (JPG ou PNG).',
    erroCarrinho: 'Não foi possível adicionar ao carrinho. Tenta de novo.',
    erros: {
      limite: 'Fizeste muitos pedidos seguidos. Tenta de novo daqui a pouco.',
      grande: 'A foto é demasiado grande. Escolhe uma foto mais leve.',
      pequena: 'A foto é demasiado pequena. Usa uma foto com mais resolução.',
    },
  },
  es: {
    titulo: 'Crea tu lienzo para pintar por números',
    subtitulo: 'Convierte tu foto en un cuadro pintado por ti: recibes el lienzo con el dibujo numerado, las pinturas exactas y los pinceles.',
    uploadBotao: 'Sube tu foto',
    uploadArrastar: 'o arrástrala aquí',
    uploadAjuda: 'JPG o PNG · hasta 15 MB',
    dica: 'Consejo: las fotos nítidas, con buena luz y caras grandes dan los cuadros más bonitos.',
    aPreparar: 'Preparando tu lienzo…',
    arrastar: 'Arrastra para girar',
    mudarImagem: 'Cambiar foto',
    vistas: { numeros: 'Por pintar', pintada: 'Pintado' },
    original: 'Ver original',
    tamanho: 'Tamaño',
    acabamento: 'Acabado',
    acabamentos: { tela: 'Solo lienzo', grade: 'Con bastidor de madera', cavalete: 'Bastidor + caballete' },
    acabamentosAjuda: {
      tela: 'Lienzo enrollado, listo para pintar sobre la mesa.',
      grade: 'Lienzo ya tensado en un bastidor de madera, listo para colgar.',
      cavalete: 'Lienzo con bastidor y un caballete de madera para pintar y exponer.',
    },
    adicionar: 'Añadir al carrito', adicionarTotal: 'Añadir al carrito · {total}', suasTelas: 'Tus lienzos', telaN: 'Lienzo {n}', adicionarTela: '+ Añadir lienzo', totalTelas: '{n} lienzos · {total}', difTela: 'Cada lienzo puede tener su propia foto, tamaño y acabado. Toca un lienzo para verlo.', fotoPrincipal: 'tu foto', rotFoto: 'Foto', mesmaFoto: 'Misma foto', outraFoto: 'Otra foto', trocarFoto: 'Cambiar foto', adicionarFoto: 'Añadir foto', remover: 'Quitar',
    aAdicionar: 'Añadiendo…',
    adicionado: 'Añadido al carrito ✓',
    verCarrinho: 'Ver carrito',
    outra: 'Crear otro lienzo',
    semImagem: 'Sube una foto para continuar',
    esgotado: 'Agotado',
    prazo: 'Hecho por encargo · enviado en {min} a {max} días',
    politicaEnvio: 'Política de envío',
    privacidade: 'Tu foto solo se usa para crear tu lienzo y se borra si no haces el pedido o en cuanto lo recibas.',
    politicaPrivacidade: 'Política de privacidad',
    kit: 'Tu kit',
    kitInclui: 'Lienzo {medida} con el dibujo numerado · pinturas acrílicas numeradas (hasta 24 colores, elegidos para tu foto) · 3 pinceles',
    kitGrade: ' · bastidor de madera y ganchos para colgar',
    kitCavalete: ' · bastidor de madera y caballete',
    cores: 'Las pinturas de tu foto',
    coresAjuda: '{n} colores elegidos automáticamente',
    aviso: 'Vista previa ilustrativa: el dibujo numerado final se prepara a partir de tu foto y puede tener zonas y números diferentes.',
    erroTipo: 'Este archivo no es una imagen. Usa una foto JPG o PNG.',
    erroTamanho: 'La foto pesa más de {mb} MB. Elige una foto más ligera.',
    erroLer: 'No hemos podido abrir esta foto. Prueba con otra (JPG o PNG).',
    erroCarrinho: 'No se ha podido añadir al carrito. Inténtalo de nuevo.',
    erros: {
      limite: 'Has hecho muchas solicitudes seguidas. Inténtalo de nuevo en un rato.',
      grande: 'La foto es demasiado grande. Elige una foto más ligera.',
      pequena: 'La foto es demasiado pequeña. Usa una foto con más resolución.',
    },
  },
  de: {
    titulo: 'Gestalte dein Malen-nach-Zahlen-Bild',
    subtitulo: 'Verwandle dein Foto in ein selbst gemaltes Bild: Du bekommst die Leinwand mit nummerierter Vorlage, die passenden Farben und Pinsel.',
    uploadBotao: 'Foto hochladen',
    uploadArrastar: 'oder hierher ziehen',
    uploadAjuda: 'JPG oder PNG · bis 15 MB',
    dica: 'Tipp: Scharfe Fotos mit gutem Licht und großen Gesichtern ergeben die schönsten Bilder.',
    aPreparar: 'Deine Leinwand wird vorbereitet…',
    arrastar: 'Zum Drehen ziehen',
    mudarImagem: 'Foto ändern',
    vistas: { numeros: 'Vorlage', pintada: 'Gemalt' },
    original: 'Original',
    tamanho: 'Größe',
    acabamento: 'Ausführung',
    acabamentos: { tela: 'Nur Leinwand', grade: 'Mit Holzrahmen', cavalete: 'Rahmen + Staffelei' },
    acabamentosAjuda: {
      tela: 'Gerollte Leinwand, zum Malen auf dem Tisch.',
      grade: 'Leinwand auf Holzrahmen gespannt, fertig zum Aufhängen.',
      cavalete: 'Leinwand mit Rahmen und Holzstaffelei zum Malen und Ausstellen.',
    },
    adicionar: 'In den Warenkorb', adicionarTotal: 'In den Warenkorb · {total}', suasTelas: 'Deine Leinwände', telaN: 'Leinwand {n}', adicionarTela: '+ Leinwand hinzufügen', totalTelas: '{n} Leinwände · {total}', difTela: 'Jede Leinwand kann ein eigenes Foto, Format und Finish haben. Tippe auf eine, um sie anzusehen.', fotoPrincipal: 'dein Foto', rotFoto: 'Foto', mesmaFoto: 'Gleiches Foto', outraFoto: 'Anderes Foto', trocarFoto: 'Foto ändern', adicionarFoto: 'Foto hinzufügen', remover: 'Entfernen',
    aAdicionar: 'Wird hinzugefügt…',
    adicionado: 'Im Warenkorb ✓',
    verCarrinho: 'Zum Warenkorb',
    outra: 'Weitere Leinwand gestalten',
    semImagem: 'Lade ein Foto hoch, um fortzufahren',
    esgotado: 'Ausverkauft',
    prazo: 'Auf Bestellung gefertigt · Versand in {min} bis {max} Tagen',
    politicaEnvio: 'Versandrichtlinie',
    privacidade: 'Dein Foto wird nur für deine Leinwand verwendet und gelöscht, wenn du nicht bestellst oder sobald du sie erhalten hast.',
    politicaPrivacidade: 'Datenschutzerklärung',
    kit: 'Dein Set',
    kitInclui: 'Leinwand {medida} mit nummerierter Vorlage · nummerierte Acrylfarben (bis zu 24 Farben, passend zu deinem Foto) · 3 Pinsel',
    kitGrade: ' · Holzrahmen und Aufhänger',
    kitCavalete: ' · Holzrahmen und Staffelei',
    cores: 'Die Farben deines Fotos',
    coresAjuda: '{n} Farben automatisch ausgewählt',
    aviso: 'Beispielhafte Vorschau: Die endgültige nummerierte Vorlage wird aus deinem Foto erstellt und kann andere Felder und Nummern haben.',
    erroTipo: 'Diese Datei ist kein Bild. Bitte nutze ein JPG- oder PNG-Foto.',
    erroTamanho: 'Das Foto ist größer als {mb} MB. Bitte wähle ein kleineres Foto.',
    erroLer: 'Wir konnten dieses Foto nicht öffnen. Versuche ein anderes (JPG oder PNG).',
    erroCarrinho: 'Konnte nicht in den Warenkorb gelegt werden. Bitte versuche es erneut.',
    erros: {
      limite: 'Zu viele Anfragen hintereinander. Bitte versuche es gleich noch einmal.',
      grande: 'Das Foto ist zu groß. Bitte wähle ein kleineres Foto.',
      pequena: 'Das Foto ist zu klein. Bitte nutze ein Foto mit höherer Auflösung.',
    },
  },
};

// kits por SKU: medidas em cm (lado menor × lado maior) e acabamento
const KITS = {
  'TELA-20X30': { tamanho: '20x30', menor: 20, maior: 30, acabamento: 'tela' },
  'TELA-20X30-GRADE': { tamanho: '20x30', menor: 20, maior: 30, acabamento: 'grade' },
  'TELA-40X50': { tamanho: '40x50', menor: 40, maior: 50, acabamento: 'tela' },
  'TELA-40X50-GRADE': { tamanho: '40x50', menor: 40, maior: 50, acabamento: 'grade' },
  'TELA-40X50-CAVALETE': { tamanho: '40x50', menor: 40, maior: 50, acabamento: 'cavalete' },
};
const ORDEM_ACABAMENTOS = ['tela', 'grade', 'cavalete'];

const ICONES = {
  foto: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="8.5" cy="10" r="1.6"/><path d="m21 16-5-5-8 8"/></svg>',
  rodar: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 0 1 15.5-6.2L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15.5 6.2L3 16"/><path d="M3 21v-5h5"/></svg>',
  camiao: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M1 6h13v10H1z"/><path d="M14 9h4l3 3v4h-7"/><circle cx="5.5" cy="17.5" r="1.8"/><circle cx="17.5" cy="17.5" r="1.8"/></svg>',
  olho: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>',
  escudo: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3 4 6v6c0 4.4 3.4 8.3 8 9 4.6-.7 8-4.6 8-9V6z"/><path d="m9 12 2 2 4-4"/></svg>',
};

/* ---------------- conversão da foto (pintura e desenho numerado) ---------------- */

function kuwahara([R, G, B], w, h, raio) {
  // filtro de Kuwahara: alisa o grão mas mantém os contornos nítidos (aspeto de pintura)
  const W1 = w + 1, tam = W1 * (h + 1);
  const sR = new Float64Array(tam), sG = new Float64Array(tam), sB = new Float64Array(tam), sL = new Float64Array(tam), sQ = new Float64Array(tam);
  for (let y = 0; y < h; y++) {
    let aR = 0, aG = 0, aB = 0, aL = 0, aQ = 0;
    for (let x = 0; x < w; x++) {
      const i = y * w + x, l = 0.3 * R[i] + 0.59 * G[i] + 0.11 * B[i];
      aR += R[i]; aG += G[i]; aB += B[i]; aL += l; aQ += l * l;
      const j = (y + 1) * W1 + x + 1, k = y * W1 + x + 1;
      sR[j] = sR[k] + aR; sG[j] = sG[k] + aG; sB[j] = sB[k] + aB; sL[j] = sL[k] + aL; sQ[j] = sQ[k] + aQ;
    }
  }
  const caixa = (s, x0, y0, x1, y1) => s[(y1 + 1) * W1 + x1 + 1] - s[y0 * W1 + x1 + 1] - s[(y1 + 1) * W1 + x0] + s[y0 * W1 + x0];
  const nR = new Float32Array(w * h), nG = new Float32Array(w * h), nB = new Float32Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    let melhor = Infinity, mR = 0, mG = 0, mB = 0;
    for (let q = 0; q < 4; q++) {
      const x0 = Math.max(0, q & 1 ? x : x - raio), x1 = Math.min(w - 1, q & 1 ? x + raio : x);
      const y0 = Math.max(0, q & 2 ? y : y - raio), y1 = Math.min(h - 1, q & 2 ? y + raio : y);
      const n = (x1 - x0 + 1) * (y1 - y0 + 1);
      const ml = caixa(sL, x0, y0, x1, y1) / n, v = caixa(sQ, x0, y0, x1, y1) / n - ml * ml;
      if (v < melhor) { melhor = v; mR = caixa(sR, x0, y0, x1, y1) / n; mG = caixa(sG, x0, y0, x1, y1) / n; mB = caixa(sB, x0, y0, x1, y1) / n; }
    }
    const i = y * w + x; nR[i] = mR; nG[i] = mG; nB[i] = mB;
  }
  return [nR, nG, nB];
}

function tratar(px, n) {
  // estica os níveis (fotos escuras ficam legíveis) e aviva um pouco as cores — só para escolher as tintas
  const R = new Float32Array(n), G = new Float32Array(n), B = new Float32Array(n);
  const hist = new Uint32Array(256);
  for (let i = 0; i < n; i++) hist[Math.round(0.3 * px[i * 4] + 0.59 * px[i * 4 + 1] + 0.11 * px[i * 4 + 2])]++;
  let acum = 0, baixo = 0, alto = 255;
  for (let v = 0; v < 256; v++) { acum += hist[v]; if (acum < n * 0.005) baixo = v; if (acum < n * 0.995) alto = v; }
  alto = Math.max(alto, baixo + 60);
  let media = 0;
  for (let i = 0; i < n; i++) media += Math.max(0, (0.3 * px[i * 4] + 0.59 * px[i * 4 + 1] + 0.11 * px[i * 4 + 2] - baixo) / (alto - baixo));
  media /= n;
  const gama = media < 0.38 ? Math.log(0.42) / Math.log(Math.max(0.1, media)) : 1;
  for (let i = 0; i < n; i++) {
    let r = Math.min(1, Math.max(0, (px[i * 4] - baixo) / (alto - baixo))) ** gama;
    let g = Math.min(1, Math.max(0, (px[i * 4 + 1] - baixo) / (alto - baixo))) ** gama;
    let b = Math.min(1, Math.max(0, (px[i * 4 + 2] - baixo) / (alto - baixo))) ** gama;
    const l = 0.3 * r + 0.59 * g + 0.11 * b;
    r = l + (r - l) * 1.12; g = l + (g - l) * 1.12; b = l + (b - l) * 1.12;
    R[i] = Math.min(255, Math.max(0, r * 255)); G[i] = Math.min(255, Math.max(0, g * 255)); B[i] = Math.min(255, Math.max(0, b * 255));
  }
  return [R, G, B];
}

function maioria(r, w, h, raio) {
  const out = new Uint8Array(r.length), conta = new Uint16Array(256);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const usados = [];
    for (let dy = -raio; dy <= raio; dy++) {
      const yy = Math.min(h - 1, Math.max(0, y + dy));
      for (let dx = -raio; dx <= raio; dx++) { const l = r[yy * w + Math.min(w - 1, Math.max(0, x + dx))]; if (!conta[l]++) usados.push(l); }
    }
    let m = r[y * w + x], mc = 0;
    for (const l of usados) { if (conta[l] > mc) { mc = conta[l]; m = l; } conta[l] = 0; }
    out[y * w + x] = m;
  }
  return out;
}

/** Corta a foto ao centro na proporção do kit (igual ao servidor) e desenha-a numa tela w×h. */
function desenharCortada(img, w, h) {
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  const g = c.getContext('2d', { willReadFrequently: true });
  const r = Math.max(w / img.width, h / img.height);
  g.imageSmoothingQuality = 'high';
  g.drawImage(img, (w - img.width * r) / 2, (h - img.height * r) / 2, img.width * r, img.height * r);
  return g;
}

function converter(img, menor, maior, K = 24) {
  const retrato = img.height >= img.width;
  const W = retrato ? Math.round(600 * menor / maior) : 600, H = retrato ? 600 : Math.round(600 * menor / maior);
  const S = 3, N = W * H, HW = W * S, HH = H * S;
  const px = desenharCortada(img, W, H).getImageData(0, 0, W, H).data;
  const [R, G, B] = kuwahara(tratar(px, N), W, H, 2);

  // tintas: k-means em Lab (como o olho vê as cores), com semente fixa
  const lin = (v) => { v /= 255; return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
  const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const L = new Float32Array(N), A = new Float32Array(N), Bb = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    const rl = lin(R[i]), gl = lin(G[i]), bl = lin(B[i]);
    const fx = f((0.4124 * rl + 0.3576 * gl + 0.1805 * bl) / 0.9505), fy = f(0.2126 * rl + 0.7152 * gl + 0.0722 * bl), fz = f((0.0193 * rl + 0.1192 * gl + 0.9505 * bl) / 1.089);
    L[i] = 116 * fy - 16; A[i] = 500 * (fx - fy); Bb[i] = 200 * (fy - fz);
  }
  let semente = 7; const aleat = () => (semente = (semente * 16807) % 2147483647) / 2147483647;
  const amostra = []; for (let i = 0; i < N; i += 2) amostra.push(i);
  const d2 = (i, c) => (L[i] - c[0]) ** 2 + (A[i] - c[1]) ** 2 + (Bb[i] - c[2]) ** 2;
  const centros = [[L[amostra[0]], A[amostra[0]], Bb[amostra[0]]]];
  const dist = new Float64Array(amostra.length).fill(Infinity);
  while (centros.length < K) {
    let soma = 0; const ult = centros[centros.length - 1];
    for (let j = 0; j < amostra.length; j++) { dist[j] = Math.min(dist[j], d2(amostra[j], ult)); soma += dist[j]; }
    let alvo = aleat() * soma, j = 0;
    while (j < amostra.length - 1 && (alvo -= dist[j]) > 0) j++;
    centros.push([L[amostra[j]], A[amostra[j]], Bb[amostra[j]]]);
  }
  for (let it = 0; it < 12; it++) {
    const acc = centros.map(() => [0, 0, 0, 0]);
    for (const i of amostra) {
      let m = 0, md = Infinity;
      for (let k = 0; k < K; k++) { const d = d2(i, centros[k]); if (d < md) { md = d; m = k; } }
      acc[m][0] += L[i]; acc[m][1] += A[i]; acc[m][2] += Bb[i]; acc[m][3]++;
    }
    acc.forEach((a, k) => { if (a[3]) centros[k] = [a[0] / a[3], a[1] / a[3], a[2] / a[3]]; });
  }
  let rot = new Uint8Array(N);
  for (let i = 0; i < N; i++) {
    let m = 0, md = Infinity;
    for (let k = 0; k < K; k++) { const d = d2(i, centros[k]); if (d < md) { md = d; m = k; } }
    rot[i] = m;
  }
  rot = maioria(rot, W, H, 2); rot = maioria(rot, W, H, 1); rot = maioria(rot, W, H, 1);

  // zonas muito pequenas juntam-se à vizinha de cor mais parecida (ninguém pinta 3 píxeis)
  const minimo = Math.round(N / 2600), fila = new Int32Array(N);
  for (let passo = 0; passo < 5; passo++) {
    const comp = new Int32Array(N).fill(-1); let n = 0;
    for (let s = 0; s < N; s++) {
      if (comp[s] >= 0) continue;
      let a = 0, b = 0; fila[b++] = s; comp[s] = n; const lb = rot[s];
      while (a < b) {
        const i = fila[a++], x = i % W, y = (i / W) | 0;
        if (x > 0 && comp[i - 1] < 0 && rot[i - 1] === lb) { comp[i - 1] = n; fila[b++] = i - 1; }
        if (x < W - 1 && comp[i + 1] < 0 && rot[i + 1] === lb) { comp[i + 1] = n; fila[b++] = i + 1; }
        if (y > 0 && comp[i - W] < 0 && rot[i - W] === lb) { comp[i - W] = n; fila[b++] = i - W; }
        if (y < H - 1 && comp[i + W] < 0 && rot[i + W] === lb) { comp[i + W] = n; fila[b++] = i + W; }
      }
      if (b < minimo) {
        const viz = new Map();
        for (let t = 0; t < b; t++) {
          const i = fila[t], x = i % W, y = (i / W) | 0;
          for (const j of [x > 0 ? i - 1 : -1, x < W - 1 ? i + 1 : -1, y > 0 ? i - W : -1, y < H - 1 ? i + W : -1]) {
            if (j >= 0 && rot[j] !== lb) viz.set(rot[j], (viz.get(rot[j]) || 0) + 1);
          }
        }
        let melhor = lb, mp = Infinity;
        for (const [l, c] of viz) {
          const p = ((centros[l][0] - centros[lb][0]) ** 2 + (centros[l][1] - centros[lb][1]) ** 2 + (centros[l][2] - centros[lb][2]) ** 2) / Math.sqrt(c);
          if (p < mp) { mp = p; melhor = l; }
        }
        for (let t = 0; t < b; t++) rot[fila[t]] = melhor;
      }
      n++;
    }
  }

  // cor de cada tinta = média dos píxeis da zona; numeradas das mais claras para as mais escuras
  const somaCor = Array.from({ length: K }, () => [0, 0, 0, 0]);
  for (let i = 0; i < N; i++) { const s = somaCor[rot[i]]; s[0] += R[i]; s[1] += G[i]; s[2] += B[i]; s[3]++; }
  const tinta = somaCor.map((s) => (s[3] ? [s[0] / s[3], s[1] / s[3], s[2] / s[3]] : [0, 0, 0]));
  const usadas = [...new Set(rot)].sort((a, b) => centros[b][0] - centros[a][0]);
  const numero = new Map(usadas.map((l, i) => [l, i + 1]));

  let alta = new Uint8Array(HW * HH);
  for (let y = 0; y < HH; y++) for (let x = 0; x < HW; x++) alta[y * HW + x] = rot[((y / S) | 0) * W + ((x / S) | 0)];
  alta = maioria(alta, HW, HH, 2); alta = maioria(alta, HW, HH, 1);

  // textura da tela (trama de linho), partilhada pelas duas vistas
  const trama = new Float32Array(HW * HH);
  for (let y = 0; y < HH; y++) for (let x = 0; x < HW; x++) {
    trama[y * HW + x] = 0.5 * Math.sin(x * 1.9 + Math.sin(y * 0.31) * 0.6) * Math.sin(y * 1.9 + Math.sin(x * 0.27) * 0.6) + (Math.random() - 0.5) * 0.6;
  }

  const porPintar = () => {
    const t = document.createElement('canvas'); t.width = HW; t.height = HH;
    const g = t.getContext('2d'); const im = g.createImageData(HW, HH); const d = im.data;
    const m = new Uint8Array(HW * HH);
    for (let i = 0; i < HW * HH; i++) {
      const l = alta[i], x = i % HW;
      if ((x < HW - 1 && alta[i + 1] !== l) || (i + HW < HW * HH && alta[i + HW] !== l)) { m[i] = 1; if (x < HW - 1) m[i + 1] = 1; if (i + HW < HW * HH) m[i + HW] = 1; }
    }
    for (let i = 0; i < HW * HH; i++) {
      const v = m[i] ? 150 : 250, tr = trama[i] * 5;
      d[i * 4] = v - 4 + tr; d[i * 4 + 1] = v - 2 + tr; d[i * 4 + 2] = v + 2 + tr; d[i * 4 + 3] = 255;
    }
    g.putImageData(im, 0, 0);
    // número no ponto mais "dentro" de cada zona (transformada de distância)
    const dd = new Float32Array(N);
    for (let i = 0; i < N; i++) {
      const x = i % W, y = (i / W) | 0, l = rot[i];
      dd[i] = x === 0 || y === 0 || x === W - 1 || y === H - 1 || rot[i - 1] !== l || rot[i + 1] !== l || rot[i - W] !== l || rot[i + W] !== l ? 0 : 1e9;
    }
    for (let y = 1; y < H; y++) for (let x = 1; x < W - 1; x++) {
      const i = y * W + x;
      if (rot[i - W] === rot[i]) dd[i] = Math.min(dd[i], dd[i - W] + 1);
      if (rot[i - 1] === rot[i]) dd[i] = Math.min(dd[i], dd[i - 1] + 1);
      if (rot[i - W - 1] === rot[i]) dd[i] = Math.min(dd[i], dd[i - W - 1] + 1.41);
      if (rot[i - W + 1] === rot[i]) dd[i] = Math.min(dd[i], dd[i - W + 1] + 1.41);
    }
    for (let y = H - 2; y >= 0; y--) for (let x = W - 2; x >= 1; x--) {
      const i = y * W + x;
      if (rot[i + W] === rot[i]) dd[i] = Math.min(dd[i], dd[i + W] + 1);
      if (rot[i + 1] === rot[i]) dd[i] = Math.min(dd[i], dd[i + 1] + 1);
      if (rot[i + W + 1] === rot[i]) dd[i] = Math.min(dd[i], dd[i + W + 1] + 1.41);
      if (rot[i + W - 1] === rot[i]) dd[i] = Math.min(dd[i], dd[i + W - 1] + 1.41);
    }
    g.fillStyle = 'rgb(110,114,130)'; g.textAlign = 'center'; g.textBaseline = 'middle';
    const ocupado = new Uint8Array(N), cand = [];
    for (let i = 0; i < N; i++) if (dd[i] >= 2.2) cand.push(i);
    cand.sort((a, b) => dd[b] - dd[a]);
    for (const i of cand) {
      if (ocupado[i]) continue;
      const x = i % W, y = (i / W) | 0, raio = Math.round(Math.max(14, dd[i] * 2.8));
      for (let dy = -raio; dy <= raio; dy++) for (let dx = -raio; dx <= raio; dx++) {
        const x2 = x + dx, y2 = y + dy;
        if (x2 >= 0 && y2 >= 0 && x2 < W && y2 < H && rot[y2 * W + x2] === rot[i]) ocupado[y2 * W + x2] = 1;
      }
      g.font = `600 ${Math.min(36, Math.max(13, dd[i] * S * 0.8))}px system-ui, sans-serif`;
      g.fillText(String(numero.get(rot[i])), (x + 0.5) * S, (y + 0.5) * S);
    }
    return t;
  };

  const pintada = () => {
    // a foto "pintada" com as cores reais: filtro de óleo, pinceladas e a trama da tela
    const PW = W * 2, PH = H * 2;
    const gp = desenharCortada(img, PW, PH);
    const ip = gp.getImageData(0, 0, PW, PH), p = ip.data, n0 = PW * PH;
    const cru = [new Float32Array(n0), new Float32Array(n0), new Float32Array(n0)];
    for (let i = 0; i < n0; i++) { cru[0][i] = p[i * 4]; cru[1][i] = p[i * 4 + 1]; cru[2][i] = p[i * 4 + 2]; }
    const [pR, pG, pB] = kuwahara(kuwahara(cru, PW, PH, 3), PW, PH, 2);
    for (let i = 0; i < n0; i++) { p[i * 4] = pR[i]; p[i * 4 + 1] = pG[i]; p[i * 4 + 2] = pB[i]; p[i * 4 + 3] = 255; }
    gp.putImageData(ip, 0, 0);
    const t = document.createElement('canvas'); t.width = HW; t.height = HH;
    const g = t.getContext('2d');
    g.imageSmoothingQuality = 'high'; g.drawImage(gp.canvas, 0, 0, HW, HH);
    let sem = 11; const rnd = () => (sem = (sem * 16807) % 2147483647) / 2147483647;
    g.lineCap = 'round';
    const lum = (i) => 0.3 * pR[i] + 0.59 * pG[i] + 0.11 * pB[i];
    const e = HW / PW;
    for (let k = 0, n = Math.round(n0 / 9); k < n; k++) {
      const x = 1 + Math.floor(rnd() * (PW - 2)), y = 1 + Math.floor(rnd() * (PH - 2)), i = y * PW + x;
      const gx = lum(i + 1) - lum(i - 1), gy = lum(i + PW) - lum(i - PW), forte = Math.abs(gx) + Math.abs(gy);
      const ang = (forte > 4 ? Math.atan2(gy, gx) + Math.PI / 2 : 0.7) + (rnd() - 0.5) * 0.4;
      const cx = (x + 0.5) * e, cy = (y + 0.5) * e, comp = e * (forte > 20 ? 1.5 + rnd() * 1.5 : 3 + rnd() * 4);
      const v = 1 + (rnd() - 0.5) * 0.12;
      g.strokeStyle = `rgba(${Math.min(255, p[i * 4] * v) | 0},${Math.min(255, p[i * 4 + 1] * v) | 0},${Math.min(255, p[i * 4 + 2] * v) | 0},0.6)`;
      g.lineWidth = e * (0.8 + rnd() * 0.8);
      g.beginPath();
      g.moveTo(cx - Math.cos(ang) * comp / 2, cy - Math.sin(ang) * comp / 2);
      g.lineTo(cx + Math.cos(ang) * comp / 2, cy + Math.sin(ang) * comp / 2);
      g.stroke();
    }
    const id = g.getImageData(0, 0, HW, HH), q = id.data;
    for (let i = 0; i < HW * HH; i++) { const tr = trama[i] * 5; q[i * 4] += tr; q[i * 4 + 1] += tr; q[i * 4 + 2] += tr; }
    g.putImageData(id, 0, 0);
    return t;
  };

  return {
    retrato,
    pintada: pintada(),
    numeros: porPintar(),
    foto: desenharCortada(img, HW, HH).canvas,
    paleta: usadas.map((l) => ({ n: numero.get(l), cor: tinta[l].map(Math.round) })),
  };
}

/* ---------------- tela 3D ---------------- */

function texturaMadeira() {
  // pinho claro com veios, como as grades de tela
  const c = document.createElement('canvas'); c.width = 256; c.height = 64; const g = c.getContext('2d');
  g.fillStyle = '#e8d3ab'; g.fillRect(0, 0, 256, 64);
  let s = 3; const rnd = () => (s = (s * 16807) % 2147483647) / 2147483647;
  for (let i = 0; i < 26; i++) {
    const y = rnd() * 64, a = 0.05 + rnd() * 0.08;
    g.strokeStyle = `rgba(150,105,55,${a})`; g.lineWidth = 0.6 + rnd() * 1.6;
    g.beginPath(); g.moveTo(0, y);
    for (let x = 0; x <= 256; x += 16) g.lineTo(x, y + Math.sin(x / 40 + i) * 2.2);
    g.stroke();
  }
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

function texturaLinho(cor) {
  const c = document.createElement('canvas'); c.width = 64; c.height = 64; const g = c.getContext('2d');
  g.fillStyle = cor; g.fillRect(0, 0, 64, 64);
  g.strokeStyle = 'rgba(0,0,0,.05)';
  for (let i = 0; i < 64; i += 3) { g.beginPath(); g.moveTo(i, 0); g.lineTo(i, 64); g.stroke(); g.beginPath(); g.moveTo(0, i); g.lineTo(64, i); g.stroke(); }
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(6, 6);
  return t;
}

function criarVisor(palco) {
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.domElement.className = 'th-tela__canvas';
  palco.prepend(renderer.domElement);
  const cena = new THREE.Scene();
  const camara = new THREE.PerspectiveCamera(30, 1, 1, 800);
  cena.add(new THREE.HemisphereLight(0xffffff, 0xe8e0d8, 2.2));
  const luz = new THREE.DirectionalLight(0xffffff, 1.6); luz.position.set(-30, 40, 60); cena.add(luz);
  const luz2 = new THREE.DirectionalLight(0xfff4ea, 0.7); luz2.position.set(40, 10, -50); cena.add(luz2);

  const pivo = new THREE.Group(); cena.add(pivo);
  const madeira = texturaMadeira();
  const matLado = new THREE.MeshStandardMaterial({ map: texturaLinho('#f4f1ea'), roughness: 0.95, side: THREE.DoubleSide });
  const matTrasTela = new THREE.MeshStandardMaterial({ map: texturaLinho('#efe7d8'), roughness: 0.95 });
  const matFrente = new THREE.MeshStandardMaterial({ roughness: 0.92, color: 0xf6f4f0 });
  const matMadeira = new THREE.MeshStandardMaterial({ map: madeira, roughness: 0.8 });
  const matCavalete = new THREE.MeshStandardMaterial({ map: madeira, color: 0xd9a86c, roughness: 0.75 });
  const matAgrafo = new THREE.MeshStandardMaterial({ color: 0x8a8d94, metalness: 0.6, roughness: 0.4 });
  const invisivel = new THREE.MeshBasicMaterial({ visible: false });

  // sombra no chão: roda com a tela (só em Y, como a sombra de um quadro pousado)
  const grupoSombra = new THREE.Group(); cena.add(grupoSombra);
  const sombra = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({
    transparent: true, depthWrite: false,
    map: (() => {
      const c = document.createElement('canvas'); c.width = 128; c.height = 128; const g = c.getContext('2d');
      const gr = g.createRadialGradient(64, 64, 2, 64, 64, 62); gr.addColorStop(0, 'rgba(20,20,50,.30)'); gr.addColorStop(0.55, 'rgba(20,20,50,.12)'); gr.addColorStop(1, 'rgba(20,20,50,0)');
      g.fillStyle = gr; g.fillRect(0, 0, 128, 128); return new THREE.CanvasTexture(c);
    })(),
  }));
  sombra.rotation.x = -Math.PI / 2; grupoSombra.add(sombra);

  let objeto = null, medidas = { w: 20, h: 30, alturaTotal: 30 };
  // tintas e pincéis pousados no chão à frente da tela: ficam fora do pivô, por isso não rodam com a tela
  const chao = new THREE.Group(); cena.add(chao);
  let paletaAtual = [];
  const matTampa = new THREE.MeshPhysicalMaterial({ color: 0xffffff, roughness: 0.35, clearcoat: 0.5 });
  const matPlastico = new THREE.MeshPhysicalMaterial({ color: 0xffffff, roughness: 0.15, transparent: true, opacity: 0.35, clearcoat: 1 });
  const matCabo = new THREE.MeshStandardMaterial({ map: madeira, color: 0xc98d55, roughness: 0.55 });
  const matVirola = new THREE.MeshStandardMaterial({ color: 0xc9ccd2, metalness: 0.85, roughness: 0.25 });
  const matPelo = new THREE.MeshStandardMaterial({ color: 0x3a2a1e, roughness: 0.9 });
  function etiqueta(n) {
    const c = document.createElement('canvas'); c.width = 64; c.height = 64; const g = c.getContext('2d');
    g.fillStyle = '#fff'; g.beginPath(); g.arc(32, 32, 30, 0, Math.PI * 2); g.fill();
    g.fillStyle = '#020c49'; g.font = '700 34px Poppins, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(n), 32, 34);
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
  }
  function arranjarChao() {
    chao.traverse((m) => { m.geometry?.dispose(); if (m.material?.map && m.userData.propria) m.material.map.dispose(); });
    chao.clear();
    if (!paletaAtual.length) return;
    const r = 1.25, alt = 1.5, passo = 3.1, n = paletaAtual.length;
    const colunas = Math.max(6, Math.min(12, Math.round(medidas.w * 1.1 / passo))), linhas = Math.ceil(n / colunas);
    const larg = (colunas - 1) * passo;
    paletaAtual.forEach(({ n: num, cor }, i) => {
      const col = i % colunas, lin = Math.floor(i / colunas);
      const x = -larg / 2 + col * passo + (lin % 2 ? passo / 2 : 0) - 3, z = 5.5 + lin * 3.9;
      const pote = new THREE.Group(); pote.position.set(x, 0, z);
      const tinta = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.94, r * 0.9, alt * 0.85, 28), new THREE.MeshStandardMaterial({ color: new THREE.Color(`rgb(${cor.join(',')})`), roughness: 0.45 }));
      tinta.position.y = alt * 0.43; pote.add(tinta);
      // a tinta à vista: brilho húmido por cima
      tinta.material.roughness = 0.25;
      const copo = new THREE.Mesh(new THREE.CylinderGeometry(r, r * 0.95, alt, 28, 1, true), matPlastico); copo.position.y = alt / 2; pote.add(copo);
      // potes abertos (vê-se a cor da tinta por cima), com a orla branca do pote
      const orla = new THREE.Mesh(new THREE.TorusGeometry(r, 0.09, 8, 32), matTampa); orla.rotation.x = Math.PI / 2; orla.position.y = alt; pote.add(orla);
      const et = new THREE.Mesh(new THREE.CircleGeometry(0.62, 24), new THREE.MeshBasicMaterial({ map: etiqueta(num), toneMapped: false }));
      et.userData.propria = true; et.position.set(0, alt * 0.5, r + 0.02); pote.add(et);
      chao.add(pote);
    });
    // 3 pincéis deitados ao lado das tintas (fino, médio e largo)
    const xPinceis = larg / 2 + 3.2;
    [[0.22, 0.35, 0.9], [0.3, 0.5, 1.2], [0.38, 0.75, 1.5]].forEach(([rc, rv, lp], i) => {
      const p = new THREE.Group();
      const cabo = new THREE.Mesh(new THREE.CylinderGeometry(rc * 0.7, rc, 13, 16), matCabo); cabo.position.y = -6.5; p.add(cabo);
      const virola = new THREE.Mesh(new THREE.CylinderGeometry(rv, rc * 1.05, 2, 16), matVirola); virola.position.y = 1; p.add(virola);
      const pelo = new THREE.Mesh(new THREE.ConeGeometry(rv * 0.95, lp + 1, 16), matPelo); pelo.position.y = 2 + (lp + 1) / 2; p.add(pelo);
      p.rotation.z = -Math.PI / 2; p.rotation.y = 0.35 + i * 0.12; // deitado no chão, a apontar para a frente e para o lado
      p.position.set(xPinceis + i * 1.6, rv, 7 + i * 2.6);
      chao.add(p);
    });
    chao.position.y = grupoSombra.position.y + 0.02;
  }
  const libertar = (o) => o?.traverse((m) => m.geometry?.dispose());

  /** Barra de madeira entre dois pontos (secção esp × prof). */
  function barra(grupo, a, b, esp, prof, mat = matMadeira) {
    const v = new THREE.Vector3().subVectors(b, a);
    const m = new THREE.Mesh(new THREE.BoxGeometry(esp, v.length(), prof), mat);
    m.position.copy(a).addScaledVector(v, 0.5);
    m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), v.normalize());
    grupo.add(m);
    return m;
  }

  /** Tela esticada numa grade: frente com a imagem, laterais de linho e a traseira de um quadro. */
  function quadro(w, h) {
    const g = new THREE.Group();
    const d = 1.8, b = Math.min(w, h) >= 40 ? 2.2 : 1.6, dd = d - 0.06;
    // pele de tela: frente + 4 laterais (a traseira fica aberta para se ver a grade)
    g.add(new THREE.Mesh(new THREE.BoxGeometry(w, h, d), [matLado, matLado, matLado, matLado, matFrente, invisivel]));
    const costas = new THREE.Mesh(new THREE.PlaneGeometry(w - 0.02, h - 0.02), matTrasTela);
    costas.rotation.y = Math.PI; costas.position.z = d / 2 - 0.03; g.add(costas);
    const z = -0.03;
    const caixa = (bw, bh, bd, x, y, zz = z, rz = 0) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, bd), matMadeira);
      m.position.set(x, y, zz); m.rotation.z = rz; g.add(m); return m;
    };
    // moldura da grade
    caixa(w - 0.1, b, dd, 0, h / 2 - b / 2 - 0.05);
    caixa(w - 0.1, b, dd, 0, -h / 2 + b / 2 + 0.05);
    caixa(b, h - 2 * b - 0.1, dd, -w / 2 + b / 2 + 0.05, 0);
    caixa(b, h - 2 * b - 0.1, dd, w / 2 - b / 2 - 0.05, 0);
    // travessa ao meio (no lado maior) nas telas grandes
    if (Math.max(w, h) >= 40) {
      if (w >= h) caixa(b * 0.9, h - 2 * b - 0.1, dd * 0.85, 0, 0, z - dd * 0.075);
      else caixa(w - 2 * b - 0.1, b * 0.9, dd * 0.85, 0, 0, z - dd * 0.075);
    }
    // cantoneiras a 45º
    const a = Math.min(w, h) * 0.2, pz = -d / 2 + dd * 0.3;
    for (const sx of [-1, 1]) for (const sy of [-1, 1]) {
      const cx = sx * (w / 2 - b), cy = sy * (h / 2 - b);
      caixa(a * Math.SQRT2, b * 0.85, dd * 0.55, cx - sx * a / 2, cy - sy * a / 2, pz, Math.atan2(-sy * a, sx * a));
    }
    // tela dobrada para trás sobre a moldura, com agrafos
    const aba = 0.9, zt = -d / 2 - 0.012;
    const tiras = [[w, aba, 0, h / 2 - aba / 2], [w, aba, 0, -h / 2 + aba / 2], [aba, h - 2 * aba, -w / 2 + aba / 2, 0], [aba, h - 2 * aba, w / 2 - aba / 2, 0]];
    for (const [tw, th, x, y] of tiras) {
      const t = new THREE.Mesh(new THREE.PlaneGeometry(tw, th), matLado);
      t.rotation.y = Math.PI; t.position.set(x, y, zt); g.add(t);
      const horizontal = tw > th, comp = horizontal ? tw : th, n = Math.max(2, Math.floor(comp / 4.5));
      for (let i = 1; i < n; i++) {
        const ag = new THREE.Mesh(new THREE.BoxGeometry(horizontal ? 0.9 : 0.12, horizontal ? 0.12 : 0.9, 0.05), matAgrafo);
        const p = -comp / 2 + (comp * i) / n;
        ag.position.set(horizontal ? x + p : x, horizontal ? y : y + p, zt - 0.02); g.add(ag);
      }
    }
    return g;
  }

  function montar(w, h, acabamento) {
    if (objeto) { pivo.remove(objeto); libertar(objeto); }
    objeto = new THREE.Group();
    const tela = quadro(w, h);
    let base = -h / 2, alturaTotal = h, pegada = [w * 1.15, 9];
    if (acabamento === 'cavalete') {
      // cavalete em A: duas pernas abertas à frente, mastro ao centro, perna de trás e o apoio da tela
      const inclinacao = 0.1, alturaApoio = h * 0.8, topo = alturaApoio + h + 7;
      const frente = new THREE.Group(); // pivô no chão; inclina para trás
      const zp = -1.8 / 2 - 1.1;
      tela.position.set(0, alturaApoio + 1.5 + h / 2, 0.6); frente.add(tela);
      const V = (x, y, z2) => new THREE.Vector3(x, y, z2);
      const abertura = Math.max(w * 0.42, 9);
      barra(frente, V(-abertura * 0.28, topo, zp), V(-abertura, 0, zp), 2.4, 1.6, matCavalete);
      barra(frente, V(abertura * 0.28, topo, zp), V(abertura, 0, zp), 2.4, 1.6, matCavalete);
      barra(frente, V(0, alturaApoio - h * 0.15, zp - 0.2), V(0, topo + 3, zp - 0.2), 2.2, 1.4, matCavalete); // mastro
      const apoio = new THREE.Mesh(new THREE.BoxGeometry(w + 6, 1.5, 4.8), matCavalete);
      apoio.position.set(0, alturaApoio + 0.75, 0.2); frente.add(apoio);
      const travessa = new THREE.Mesh(new THREE.BoxGeometry(abertura * 1.5, 1.6, 1.3), matCavalete);
      travessa.position.set(0, alturaApoio * 0.32, zp); frente.add(travessa);
      const grampo = new THREE.Mesh(new THREE.BoxGeometry(5, 1.6, 4.2), matCavalete);
      grampo.position.set(0, alturaApoio + 1.5 + h + 0.8, 0.2); frente.add(grampo);
      frente.rotation.x = -inclinacao;
      objeto.add(frente);
      // perna de trás: do topo do mastro até ao chão, atrás
      const topoMundo = V(0, topo - 4, zp - 1).applyAxisAngle(V(1, 0, 0), -inclinacao);
      barra(objeto, topoMundo, V(0, 0, -topo * 0.42), 2.2, 1.4, matCavalete);
      base = 0; alturaTotal = topo * Math.cos(inclinacao) + 3;
      pegada = [abertura * 2.6, topo * 0.55];
    } else {
      objeto.add(tela);
    }
    pivo.add(objeto);
    // centra o conjunto na vista
    objeto.position.y = -(base + alturaTotal / 2);
    grupoSombra.position.y = -alturaTotal / 2 - 0.05;
    sombra.scale.set(pegada[0], pegada[1], 1);
    sombra.position.z = acabamento === 'cavalete' ? -pegada[1] * 0.28 : 0;
    medidas = { w, h, alturaTotal };
    arranjarChao();
    ajustar();
  }

  function ajustar() {
    // o canvas ocupa o palco menos as barras de cima (vistas) e de baixo (rodar / mudar foto)
    const larg = renderer.domElement.clientWidth || palco.clientWidth || 1;
    const alt = renderer.domElement.clientHeight || palco.clientHeight || 1;
    renderer.setSize(larg, alt, false); camara.aspect = larg / alt;
    const meioFov = THREE.MathUtils.degToRad(camara.fov / 2);
    const comTintas = paletaAtual.length > 0, extra = comTintas ? 1.32 : 1.16;
    const distV = (medidas.alturaTotal * extra) / 2 / Math.tan(meioFov);
    const distH = (Math.max(medidas.w, medidas.h) * (comTintas ? 1.5 : 1.32)) / 2 / Math.tan(meioFov) / camara.aspect;
    const d = Math.max(distV, distH), alvoY = comTintas ? -medidas.alturaTotal * 0.1 : 0;
    camara.position.set(0, alvoY + d * (comTintas ? 0.27 : 0.08), d); camara.lookAt(0, alvoY, 0); camara.updateProjectionMatrix();
  }
  const ro = new ResizeObserver(ajustar); ro.observe(renderer.domElement);

  let rotY = -0.22, rotX = 0.04, arrastar = null, interagiu = false;
  const el = renderer.domElement;
  el.addEventListener('pointerdown', (e) => { arrastar = { x: e.clientX, y: e.clientY, ry: rotY, rx: rotX }; interagiu = true; el.setPointerCapture(e.pointerId); });
  el.addEventListener('pointermove', (e) => {
    if (!arrastar) return;
    rotY = arrastar.ry + (e.clientX - arrastar.x) * 0.008;
    rotX = Math.max(-0.35, Math.min(0.35, arrastar.rx + (e.clientY - arrastar.y) * 0.005));
  });
  const soltar = () => { arrastar = null; };
  el.addEventListener('pointerup', soltar); el.addEventListener('pointercancel', soltar);
  const movimento = !matchMedia('(prefers-reduced-motion: reduce)').matches;
  const posicionar = () => { pivo.rotation.set(rotX, rotY, 0); grupoSombra.rotation.y = rotY; };
  renderer.setAnimationLoop((t) => {
    if (!arrastar && !interagiu && movimento) rotY = -0.22 + Math.sin(t / 1700) * 0.2;
    posicionar();
    renderer.render(cena, camara);
  });

  // texturas em cache (mudar de vista ou carregar em "ver original" é instantâneo)
  const texturas = new Map();
  const texturaDe = (canvas) => {
    let tx = texturas.get(canvas);
    if (!tx) {
      tx = new THREE.CanvasTexture(canvas);
      tx.colorSpace = THREE.SRGBColorSpace; tx.anisotropy = renderer.capabilities.getMaxAnisotropy();
      texturas.set(canvas, tx);
    }
    return tx;
  };
  const aplicar = (tx) => { matFrente.map = tx; matFrente.color.set(0xffffff); matFrente.needsUpdate = true; };
  let revelacao = null;

  return {
    montar,
    tintas(paleta) { paletaAtual = paleta || []; arranjarChao(); ajustar(); },
    textura(canvas) { revelacao?.parar(); aplicar(texturaDe(canvas)); },
    limparTexturas() { revelacao?.parar(); for (const tx of texturas.values()) tx.dispose(); texturas.clear(); },
    reiniciarRotacao() { interagiu = false; },
    /** Passa da imagem `de` para `para` com uma pincelada em diagonal. Resolve quando acaba (ou é interrompida). */
    revelar(de, para, ms = 1900) {
      revelacao?.parar();
      return new Promise((ok) => {
        const k = Math.min(1, 1024 / Math.max(para.width, para.height));
        const W = Math.round(para.width * k), H = Math.round(para.height * k);
        const comp = document.createElement('canvas'); comp.width = W; comp.height = H;
        const tmp = document.createElement('canvas'); tmp.width = W; tmp.height = H;
        const g = comp.getContext('2d'), gt = tmp.getContext('2d');
        const tx = new THREE.CanvasTexture(comp); tx.colorSpace = THREE.SRGBColorSpace;
        aplicar(tx);
        const inicio = performance.now();
        let acabou = false, pedido = 0;
        const fim = (completa) => {
          if (acabou) return; acabou = true; cancelAnimationFrame(pedido); clearTimeout(seguranca);
          revelacao = null;
          if (completa) aplicar(texturaDe(para));
          tx.dispose(); ok(completa);
        };
        const passo = () => {
          const t = Math.min(1, (performance.now() - inicio) / ms);
          const e = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
          const p = -0.15 + e * 1.3, banda = 0.12;
          g.globalCompositeOperation = 'source-over';
          g.drawImage(de, 0, 0, W, H);
          gt.globalCompositeOperation = 'source-over'; gt.clearRect(0, 0, W, H);
          gt.drawImage(para, 0, 0, W, H);
          gt.globalCompositeOperation = 'destination-in';
          const gr = gt.createLinearGradient(0, 0, W, H);
          gr.addColorStop(0, 'rgba(0,0,0,1)');
          gr.addColorStop(Math.min(1, Math.max(0, p)), 'rgba(0,0,0,1)');
          gr.addColorStop(Math.min(1, Math.max(0, p + banda)), 'rgba(0,0,0,0)');
          gr.addColorStop(1, 'rgba(0,0,0,0)');
          gt.fillStyle = gr; gt.fillRect(0, 0, W, H);
          g.drawImage(tmp, 0, 0);
          // brilho na frente da pincelada
          const brilho = g.createLinearGradient(0, 0, W, H);
          const c = Math.min(1, Math.max(0, p + banda * 0.5));
          brilho.addColorStop(Math.max(0, c - 0.03), 'rgba(255,255,255,0)');
          brilho.addColorStop(c, 'rgba(255,255,255,0.55)');
          brilho.addColorStop(Math.min(1, c + 0.03), 'rgba(255,255,255,0)');
          g.fillStyle = brilho; g.fillRect(0, 0, W, H);
          tx.needsUpdate = true;
          if (t >= 1) fim(true); else pedido = requestAnimationFrame(passo);
        };
        // se o separador ficar escondido (sem frames), termina na mesma
        const seguranca = setTimeout(() => fim(true), ms + 1500);
        revelacao = { parar: () => fim(false) };
        pedido = requestAnimationFrame(passo);
      });
    },
    /** Imagem da tela (fundo transparente, sem sombra) para o carrinho, com a vista indicada. */
    async capturaBlob(vista, voltar, rodar = -0.32) {
      const antes = { ry: rotY, rx: rotX };
      if (vista) this.textura(vista);
      sombra.visible = false; chao.visible = false; rotY = rodar; rotX = 0.04;
      posicionar();
      renderer.render(cena, camara);
      const blob = await new Promise((ok) => renderer.domElement.toBlob(ok, 'image/png'));
      sombra.visible = true; chao.visible = true; rotY = antes.ry; rotX = antes.rx;
      if (vista && voltar) this.textura(voltar);
      return blob;
    },
    destruir() {
      revelacao?.parar(); renderer.setAnimationLoop(null); ro.disconnect(); libertar(objeto);
      for (const tx of texturas.values()) tx.dispose();
      renderer.forceContextLoss(); renderer.dispose(); renderer.domElement.remove();
    },
  };
}

/* ---------------- interface ---------------- */

function el(tag, classe, html) {
  const e = document.createElement(tag);
  if (classe) e.className = classe;
  if (html != null) e.innerHTML = html;
  return e;
}
const icone = (nome) => `<span class="th-alm__icone" aria-hidden="true">${ICONES[nome]}</span>`;
const formatar = (s, v) => s.replace(/\{(\w+)\}/g, (_, k) => v[k] ?? '');

function carregarImagem(ficheiro) {
  return new Promise((ok, erro) => {
    const url = URL.createObjectURL(ficheiro);
    const im = new Image();
    im.onload = () => {
      // limita o lado maior a 2400 px (chega para a pré-visualização e poupa memória no telemóvel)
      const r = Math.min(1, 2400 / Math.max(im.naturalWidth, im.naturalHeight));
      const c = document.createElement('canvas');
      c.width = Math.round(im.naturalWidth * r); c.height = Math.round(im.naturalHeight * r);
      c.getContext('2d').drawImage(im, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url); ok(c);
    };
    im.onerror = () => { URL.revokeObjectURL(url); erro(new Error('ilegivel')); };
    im.src = url;
  });
}

export function iniciar(raiz, cfg) {
  const lingua = String(cfg.lingua || 'pt').slice(0, 2).toLowerCase();
  const T = TEXTOS[lingua] || TEXTOS.pt;
  const raizLoja = window.Shopify?.routes?.root || '/';
  const variantes = (cfg.variantes || []).map((v) => ({ ...v, kit: KITS[String(v.sku || '').toUpperCase()] })).filter((v) => v.kit);
  const tamanhos = [...new Set(variantes.map((v) => v.kit.tamanho))];
  const padrao = variantes.find((v) => v.id === cfg.varianteAtual) || variantes[0];
  // várias telas antes do carrinho: cada uma com a sua foto (null = a da 1.ª), tamanho e acabamento;
  // estado.tamanho/acabamento/telas/imagem/ficheiro são os da tela que se está a ver (estado.atual)
  const novaTela = (de) => ({ tamanho: de?.tamanho ?? padrao?.kit.tamanho, acabamento: de?.acabamento ?? padrao?.kit.acabamento, ficheiro: null, imagem: null, telas: null, noCarrinho: false });
  const estado = { itens: [novaTela()], atual: 0, vista: 'numeros', visor: null, adicionado: false, ocupado: false };
  const item = () => estado.itens[estado.atual];
  // "adicionado" = todas as telas já estão no carrinho; uma tela que mude volta a poder ser adicionada (só ela)
  const sincronizar = () => { estado.adicionado = estado.itens.every((it) => it.noCarrinho); };
  for (const campo of ['tamanho', 'acabamento', 'telas']) Object.defineProperty(estado, campo, { get: () => item()[campo], set: (v) => { item()[campo] = v; } });
  for (const campo of ['imagem', 'ficheiro']) Object.defineProperty(estado, campo, { get: () => item()[campo] || estado.itens[0][campo], set: (v) => { item()[campo] = v; } });
  const variante = () => variantes.find((v) => v.kit.tamanho === estado.tamanho && v.kit.acabamento === estado.acabamento);

  const palco = raiz.querySelector('.th-alm__palco');
  const painel = raiz.querySelector('.th-alm__painel');
  palco.classList.add('th-tela__palco');
  painel.replaceChildren();

  const entrada = el('input', 'th-alm__ficheiro');
  entrada.type = 'file'; entrada.accept = 'image/jpeg,image/png,image/webp'; entrada.tabIndex = -1;
  entrada.setAttribute('aria-hidden', 'true');
  raiz.append(entrada);

  /* painel */
  const titulo = el('h1', 'th-alm__titulo', ''); titulo.textContent = T.titulo;
  const subtitulo = el('p', 'th-alm__subtitulo'); subtitulo.textContent = T.subtitulo;
  const preco = el('p', 'th-alm__preco');
  const blocoTamanho = el('div', 'th-alm__bloco');
  const rotTamanho = el('p', 'th-alm__rotulo'); rotTamanho.textContent = T.tamanho;
  const listaTamanhos = el('div', 'th-alm__tamanhos'); listaTamanhos.setAttribute('role', 'radiogroup'); listaTamanhos.setAttribute('aria-label', T.tamanho);
  blocoTamanho.append(rotTamanho, listaTamanhos);
  const blocoAcab = el('div', 'th-alm__bloco');
  const rotAcab = el('p', 'th-alm__rotulo'); rotAcab.textContent = T.acabamento;
  const listaAcab = el('div', 'th-tela__acabamentos'); listaAcab.setAttribute('role', 'radiogroup'); listaAcab.setAttribute('aria-label', T.acabamento);
  const ajudaAcab = el('p', 'th-alm__medidas');
  blocoAcab.append(rotAcab, listaAcab, ajudaAcab);
  const cta = el('button', 'th-alm__cta'); cta.type = 'button';
  const erroCarrinho = el('p', 'th-alm__erro-carrinho'); erroCarrinho.hidden = true; erroCarrinho.setAttribute('role', 'alert');
  const depois = el('div', 'th-tela__depois'); depois.hidden = true;
  const btnOutra = el('button', 'th-alm__secundario th-alm__outra-botao'); btnOutra.type = 'button'; btnOutra.textContent = T.outra;
  const verCarrinho = el('a', 'th-alm__ver-carrinho'); verCarrinho.href = `${raizLoja}cart`; verCarrinho.textContent = T.verCarrinho;
  depois.append(btnOutra, verCarrinho);
  const prazo = el('p', 'th-alm__prazo');
  if (cfg.prazoMin && cfg.prazoMax) {
    prazo.innerHTML = `${icone('camiao')}<span></span>`;
    const span = prazo.lastElementChild;
    span.textContent = `${formatar(T.prazo, { min: cfg.prazoMin, max: cfg.prazoMax })} · `;
    const a = el('a'); a.href = `${raizLoja}policies/shipping-policy`; a.textContent = T.politicaEnvio; span.append(a);
  } else prazo.hidden = true;
  const privacidade = el('p', 'th-alm__prazo th-alm__privacidade', `${icone('escudo')}<span></span>`);
  {
    const span = privacidade.lastElementChild;
    span.textContent = `${T.privacidade} `;
    const a = el('a'); a.href = `${raizLoja}policies/privacy-policy`; a.textContent = T.politicaPrivacidade; span.append(a);
  }
  const kit = el('div', 'th-tela__kit');
  const kitTitulo = el('p', 'th-alm__rotulo'); kitTitulo.textContent = T.kit;
  const kitTexto = el('p', 'th-tela__kit-texto');
  const coresTitulo = el('p', 'th-alm__rotulo'); coresTitulo.hidden = true;
  const paleta = el('div', 'th-tela__paleta');
  kit.append(kitTitulo, kitTexto, coresTitulo, paleta);
  const aviso = el('p', 'th-alm__aviso'); aviso.textContent = T.aviso;
  const blocoTelas = el('div', 'th-alm__bloco');
  painel.append(titulo, subtitulo, preco, blocoTelas, blocoTamanho, blocoAcab, cta, erroCarrinho, depois, prazo, privacidade, kit, aviso);

  /* várias telas */
  const varianteDe = (it) => variantes.find((v) => v.kit.tamanho === it.tamanho && v.kit.acabamento === it.acabamento);
  const valor = (p) => parseFloat(String(p || '0').replace(/[^\d,.]/g, '').replace(/\.(?=\d{3})/g, '').replace(',', '.')) || 0;
  const comoPreco = (n, modelo) => String(modelo || '€0,00').replace(/\d[\d.,\s]*\d|\d/, n.toFixed(2).replace('.', /\d,\d{2}(?!\d)/.test(modelo || '') ? ',' : '.'));
  const miniFoto = (img) => { const c = document.createElement('canvas'); c.width = c.height = 96; if (img) { const g = c.getContext('2d'), r = Math.max(96 / img.width, 96 / img.height); g.drawImage(img, (96 - img.width * r) / 2, (96 - img.height * r) / 2, img.width * r, img.height * r); } return c; };
  let alvoFoto = 0; // a tela que recebe a próxima foto escolhida
  function desenharTelas() {
    // bundle numa só linha (como no design): um cartão por tela e um tracejado para juntar mais
    const n = estado.itens.length, parar = (fn) => (ev) => { ev.stopPropagation(); fn(); };
    const cab = el('div', 'th-foto__lugares-cab'); const r = el('p', 'th-alm__rotulo'); r.textContent = T.suasTelas; cab.append(r);
    const lista = el('div', 'th-foto__lugares'); lista.setAttribute('role', 'list');
    const resumo = (it) => `${it.tamanho.replace('x', ' × ')} · ${T.acabamentos[it.acabamento]}`;
    estado.itens.forEach((it, i) => {
      const w = el('div', 'th-foto__lugar'); w.setAttribute('role', 'listitem');
      const bt = el('button', 'th-foto__lugar-bt'); bt.type = 'button'; bt.classList.toggle('is-ativo', i === estado.atual);
      bt.setAttribute('aria-pressed', String(i === estado.atual)); bt.setAttribute('aria-label', `${formatar(T.telaN, { n: i + 1 })} · ${resumo(it)}`);
      const mini = el('span', 'th-foto__lugar-mini'); const img = it.imagem || estado.itens[0].imagem;
      if (img) mini.append(miniFoto(img)); else { mini.classList.add('is-vazia'); mini.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="8.5" cy="10" r="1.6"/><path d="m21 16-5-5-8 8"/></svg>'; }
      const t = el('span', 'th-foto__lugar-texto'); t.textContent = resumo(it);
      bt.append(mini, t); bt.addEventListener('click', () => { if (i !== estado.atual) ver(i); });
      w.append(bt);
      if (n > 1) {
        const tirar = el('button', 'th-foto__lugar-tirar'); tirar.type = 'button'; tirar.textContent = '×'; tirar.setAttribute('aria-label', `${T.remover} ${formatar(T.telaN, { n: i + 1 })}`);
        tirar.addEventListener('click', parar(() => {
          if (estado.ocupado) return;
          if (i === 0) { const seg = estado.itens[1]; if (seg && !seg.imagem) { seg.imagem = estado.itens[0].imagem; seg.ficheiro = estado.itens[0].ficheiro; } }
          estado.itens.splice(i, 1);
          const nova = estado.atual > i ? estado.atual - 1 : Math.min(estado.atual, estado.itens.length - 1);
          if (alvoFoto >= i && alvoFoto > 0) alvoFoto--;
          estado.atual = -1; sincronizar(); ver(nova, true);
        }));
        w.append(tirar);
      }
      lista.append(w);
    });
    // só o próximo lugar tracejado (ao juntar uma, aparece a seguinte): diz quanto custa mais uma (uma cópia da que se está a ver)
    const juntar = (k) => {
      if (estado.ocupado) return;
      const de = item();
      for (let j = 0; j < k; j++) {
        const nova = novaTela(de);
        if (estado.atual > 0 && de.imagem) { nova.imagem = de.imagem; nova.ficheiro = de.ficheiro; }
        nova.telas = de.telas;
        estado.itens.push(nova);
      }
      sincronizar(); ver(estado.itens.length - 1, true);
    };
    for (let k = 0; n + k < 6 && k === 0; k++) {
      const bt = el('button', 'th-foto__lugar-mais'); bt.type = 'button';
      bt.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';
      const nm = el('strong'); nm.textContent = formatar(T.telaN, { n: n + k + 1 }); bt.append(nm);
      if (k === 0) { const d = el('small'); d.textContent = `+${variante()?.preco || ''}`; bt.append(d); }
      bt.addEventListener('click', () => juntar(k + 1));
      lista.append(bt);
    }
    const kids = [cab, lista];
    // a tela escolhida (a partir da 2.ª): a mesma foto da 1.ª ou outra
    const i = estado.atual, it = estado.itens[i];
    if (i > 0 && it) {
      const linha = el('div', 'th-foto__linha'); const rf = el('p', 'th-alm__rotulo'); rf.textContent = T.rotFoto;
      const bts = el('div', 'th-foto__linha-botoes'); bts.setAttribute('role', 'radiogroup'); bts.setAttribute('aria-label', T.rotFoto);
      const mesma = el('button', 'th-alm__pilula'); mesma.type = 'button'; mesma.textContent = T.mesmaFoto; mesma.classList.toggle('is-ativo', !it.imagem);
      mesma.addEventListener('click', () => { if (estado.ocupado || !it.imagem) return; it.imagem = null; it.ficheiro = null; it.telas = null; it.noCarrinho = false; sincronizar(); ver(i, true); });
      const outra = el('button', 'th-alm__pilula'); outra.type = 'button'; outra.textContent = T.outraFoto; outra.classList.toggle('is-ativo', !!it.imagem);
      outra.addEventListener('click', () => { if (estado.ocupado) return; alvoFoto = i; entrada.click(); });
      bts.append(mesma, outra); linha.append(rf, bts); kids.push(linha);
    }
    blocoTelas.replaceChildren(...kids);
  }
  // mostra a tela i (no 3D e nas opções); se ainda não foi preparada para o seu tamanho, prepara-a
  function ver(i, forcar = false) {
    if ((i === estado.atual && !forcar) || estado.ocupado) return;
    estado.atual = i;
    desenharOpcoes(); atualizarPainel();
    if (!estado.imagem) { faseVazio(); return; }
    if (!estado.telas) { processar(false); return; }
    if (!estado.visor || !barra?.isConnected) { estado.visor?.destruir(); estado.visor = null; montarPalco(); }
    aPreparar.hidden = true;
    estado.visor.limparTexturas(); // só ficam na placa gráfica as texturas da tela à vista
    estado.visor.montar(...dimensoes(), estado.acabamento); estado.visor.textura(estado.telas[estado.vista]); estado.visor.tintas(estado.telas.paleta);
    mostrarPaleta();
  }

  function desenharOpcoes() {
    listaTamanhos.replaceChildren(...tamanhos.map((t) => {
      const b = el('button', 'th-alm__pilula'); b.type = 'button'; b.setAttribute('role', 'radio');
      b.textContent = `${t.replace('x', ' × ')} cm`;
      const ativo = t === estado.tamanho;
      b.classList.toggle('is-ativo', ativo); b.setAttribute('aria-checked', String(ativo));
      b.addEventListener('click', () => {
        if (estado.ocupado || estado.tamanho === t) return;
        estado.tamanho = t;
        if (!variante()) estado.acabamento = variantes.find((v) => v.kit.tamanho === t)?.kit.acabamento;
        mudouVariante(true);
      });
      return b;
    }));
    const acabs = ORDEM_ACABAMENTOS.filter((a) => variantes.some((v) => v.kit.tamanho === estado.tamanho && v.kit.acabamento === a));
    listaAcab.replaceChildren(...acabs.map((a) => {
      const v = variantes.find((x) => x.kit.tamanho === estado.tamanho && x.kit.acabamento === a);
      const b = el('button', 'th-tela__opcao'); b.type = 'button'; b.setAttribute('role', 'radio');
      const nome = el('span', 'th-tela__opcao-nome'); nome.textContent = T.acabamentos[a];
      const p = el('span', 'th-tela__opcao-preco'); p.textContent = v.disponivel ? v.preco : T.esgotado;
      b.append(nome, p); b.disabled = !v.disponivel;
      const ativo = a === estado.acabamento;
      b.classList.toggle('is-ativo', ativo); b.setAttribute('aria-checked', String(ativo));
      b.addEventListener('click', () => { if (!estado.ocupado && estado.acabamento !== a) { estado.acabamento = a; mudouVariante(false); } });
      return b;
    }));
    ajudaAcab.textContent = T.acabamentosAjuda[estado.acabamento] || '';
    desenharTelas();
  }

  function atualizarPainel() {
    const v = variante();
    preco.textContent = v?.preco || '';
    const n = estado.itens.length, total = n > 1 ? comoPreco(estado.itens.reduce((t, it) => t + valor(varianteDe(it)?.preco), 0), v?.preco) : '';
    if (n > 1) preco.textContent = formatar(T.totalTelas, { n, total });
    const k = v?.kit;
    if (k) {
      const medida = `${k.menor} × ${k.maior} cm`;
      kitTexto.textContent = formatar(T.kitInclui, { medida }) + (k.acabamento === 'grade' ? T.kitGrade : k.acabamento === 'cavalete' ? T.kitCavalete : '');
    }
    if (estado.ocupado) { cta.textContent = T.aAdicionar; cta.disabled = true; return; }
    if (estado.aCarregar) { cta.disabled = true; return; }
    if (estado.adicionado) {
      cta.textContent = T.adicionado; cta.disabled = true; cta.classList.add('is-adicionado');
      depois.hidden = false;
      return;
    }
    cta.classList.remove('is-adicionado'); depois.hidden = true;
    if (!v?.disponivel) { cta.textContent = T.esgotado; cta.disabled = true; }
    else if (!estado.itens[0].imagem) { cta.textContent = T.semImagem; cta.disabled = false; }
    else {
      // o botão mostra o valor do que vai ser adicionado agora (as telas que já estão no carrinho não contam)
      const falta = estado.itens.filter((it) => !it.noCarrinho), totalFalta = comoPreco(falta.reduce((t, it) => t + valor(varianteDe(it)?.preco), 0), v?.preco);
      cta.textContent = n > 1 ? formatar(T.adicionarTotal, { total: totalFalta }) : T.adicionar; cta.disabled = false;
    }
  }

  function mudouVariante(mudouTamanho) {
    if (estado.ocupado) return;
    item().noCarrinho = false; sincronizar();
    desenharOpcoes(); atualizarPainel();
    if (estado.imagem && mudouTamanho) { estado.telas = null; processar(); }
    else if (estado.telas) estado.visor?.montar(...dimensoes(), estado.acabamento);
    else if (!estado.imagem && estado.visor) {
      // sem foto: a tela vazia acompanha o tamanho e o acabamento
      const k = variante()?.kit || { menor: 20, maior: 30 };
      estado.visor.montar(k.menor, k.maior, estado.acabamento); estado.visor.textura(simboloFoto(k.menor, k.maior));
    }
    if (cfg.atualizarUrl !== false && variante()) {
      const u = new URL(location.href); u.searchParams.set('variant', variante().id);
      history.replaceState(history.state, '', u);
    }
  }

  /* palco */
  function dimensoes() {
    const k = variante()?.kit || { menor: 20, maior: 30 };
    return estado.telas?.retrato === false ? [k.maior, k.menor] : [k.menor, k.maior];
  }

  function faseVazio(mensagemErro) {
    estado.visor?.destruir(); estado.visor = null;
    palco.replaceChildren();
    const zona = el('div', 'th-alm__zona th-tela__zona-3d');
    zona.tabIndex = 0; zona.setAttribute('role', 'button');
    // a tela 3D verdadeira (no tamanho e acabamento escolhidos), branca, com o símbolo de foto
    const caixa = el('div', 'th-tela__vazio-3d'); zona.append(caixa);
    requestAnimationFrame(() => {
      if (!caixa.isConnected) return;
      estado.visor = criarVisor(caixa);
      const k = variante()?.kit || { menor: 20, maior: 30 };
      estado.visor.montar(k.menor, k.maior, estado.acabamento); estado.visor.textura(simboloFoto(k.menor, k.maior));
    });
    const tz = el('p', 'th-alm__zona-titulo'); tz.textContent = T.uploadBotao;
    const az = el('p', 'th-alm__zona-ajuda'); az.textContent = T.uploadArrastar;
    const fz = el('p', 'th-alm__zona-ajuda th-alm__zona-formatos'); fz.textContent = T.uploadAjuda;
    zona.append(tz, az, fz);
    if (mensagemErro) { const e = el('p', 'th-alm__erro-carrinho'); e.textContent = mensagemErro; e.setAttribute('role', 'alert'); zona.append(e); }
    const dica = el('p', 'th-alm__dica'); dica.textContent = T.dica;
    // arrastar a tela 3D (para a rodar) não abre a escolha da foto; só um toque
    let toque = null;
    zona.addEventListener('pointerdown', (e) => { toque = [e.clientX, e.clientY]; });
    zona.addEventListener('click', (e) => { if (toque && Math.hypot(e.clientX - toque[0], e.clientY - toque[1]) > 6) return; escolherAqui(); });
    zona.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); escolherAqui(); } });
    zona.addEventListener('dragover', (e) => { e.preventDefault(); zona.classList.add('is-arrastar'); });
    zona.addEventListener('dragleave', () => zona.classList.remove('is-arrastar'));
    zona.addEventListener('drop', (e) => { e.preventDefault(); zona.classList.remove('is-arrastar'); alvoFoto = estado.atual; escolher(e.dataTransfer.files?.[0]); });
    palco.append(zona, dica);
    coresTitulo.hidden = true; paleta.replaceChildren();
    atualizarPainel();
  }

  // imagem branca com o símbolo de foto (moldura, sol e montanha), na proporção da tela
  function simboloFoto(w, h) {
    const c = document.createElement('canvas'); c.width = 1000; c.height = Math.round(1000 * h / w); const g = c.getContext('2d');
    g.fillStyle = '#ffffff'; g.fillRect(0, 0, c.width, c.height);
    const L = Math.min(c.width, c.height) * 0.62, cx = c.width / 2, cy = c.height / 2, k = L / 600;
    g.fillStyle = '#f6efe9'; g.beginPath(); g.arc(cx, cy, L / 2, 0, Math.PI * 2); g.fill();
    g.strokeStyle = '#7a1f0e'; g.lineWidth = 34 * k; g.lineJoin = 'round'; g.lineCap = 'round';
    g.beginPath(); g.roundRect(cx - 170 * k, cy - 140 * k, 340 * k, 280 * k, 46 * k); g.stroke();
    g.beginPath(); g.arc(cx - 75 * k, cy - 50 * k, 34 * k, 0, Math.PI * 2); g.stroke();
    g.beginPath(); g.moveTo(cx - 155 * k, cy + 100 * k); g.lineTo(cx - 30 * k, cy); g.lineTo(cx + 60 * k, cy + 70 * k); g.lineTo(cx + 100 * k, cy + 35 * k); g.lineTo(cx + 160 * k, cy + 90 * k); g.stroke();
    return c;
  }
  let barra = null, aPreparar = null, vistas = null;
  const marcarVista = () => vistas?.querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x.dataset.vista === estado.vista)));
  function montarPalco() {
    palco.replaceChildren();
    estado.visor = criarVisor(palco);
    vistas = el('div', 'th-tela__vistas'); vistas.setAttribute('role', 'group');
    for (const v of ['numeros', 'pintada']) {
      const b = el('button'); b.type = 'button'; b.dataset.vista = v; b.textContent = T.vistas[v];
      b.addEventListener('click', () => {
        estado.vista = v; marcarVista();
        if (estado.telas) estado.visor.textura(estado.telas[v]);
      });
      vistas.append(b);
    }
    marcarVista();
    // "ver original": mostra a foto só enquanto o dedo (ou o rato) está em cima do botão
    const original = el('button', 'th-tela__original', `${icone('olho')}<span></span>`);
    original.type = 'button'; original.lastElementChild.textContent = T.original;
    let aVer = false;
    const ver = (e) => {
      if (!estado.telas || aVer) return;
      e?.preventDefault?.();
      aVer = true; original.classList.add('is-ativo'); estado.visor.textura(estado.telas.foto);
      if (e?.pointerId != null) original.setPointerCapture?.(e.pointerId);
    };
    const voltar = () => {
      if (!aVer) return;
      aVer = false; original.classList.remove('is-ativo');
      if (estado.telas) estado.visor.textura(estado.telas[estado.vista]);
    };
    original.addEventListener('pointerdown', ver);
    for (const ev of ['pointerup', 'pointercancel', 'lostpointercapture', 'blur']) original.addEventListener(ev, voltar);
    original.addEventListener('keydown', (e) => { if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) ver(e); });
    original.addEventListener('keyup', (e) => { if (e.key === ' ' || e.key === 'Enter') voltar(); });
    original.addEventListener('contextmenu', (e) => e.preventDefault());
    barra = el('div', 'th-alm__barra-ferramentas');
    const arr = el('p', 'th-alm__arrastar', `${icone('rodar')}<span></span>`); arr.lastElementChild.textContent = T.arrastar;
    const mudar = el('button', 'th-alm__secundario'); mudar.type = 'button'; mudar.textContent = T.mudarImagem;
    mudar.addEventListener('click', () => escolherAqui());
    const botoes = el('div', 'th-alm__botoes-palco'); botoes.append(mudar);
    barra.append(arr, botoes);
    aPreparar = el('div', 'th-tela__a-preparar'); aPreparar.textContent = T.aPreparar; aPreparar.hidden = true;
    palco.append(vistas, original, barra, aPreparar);
  }

  async function processar(animar = false) {
    if (!estado.visor || !barra?.isConnected) { estado.visor?.destruir(); estado.visor = null; montarPalco(); }
    // tudo o que se usa é desta tela (se entretanto se passar para outra, este pedido pára)
    const itemAntes = item(), imagemAntes = estado.imagem, k = variante()?.kit || { menor: 20, maior: 30 }, retrato = imagemAntes.height >= imagemAntes.width;
    const ainda = () => item() === itemAntes && estado.visor;
    // enquanto prepara: a tela 3D verdadeira, já no formato da foto, branca com o símbolo de foto
    const [w0, h0] = retrato ? [k.menor, k.maior] : [k.maior, k.menor];
    estado.visor.montar(w0, h0, estado.acabamento); estado.visor.textura(simboloFoto(w0, h0)); estado.visor.tintas([]);
    aPreparar.hidden = false;
    cta.disabled = true;
    await new Promise((r) => setTimeout(r, 80)); // deixa o browser mostrar a tela e "A preparar…"
    if (!ainda()) { if (item()?.telas) aPreparar.hidden = true; return; }
    itemAntes.telas = converter(imagemAntes, k.menor, k.maior);
    estado.visor.limparTexturas();
    estado.visor.montar(...dimensoes(), estado.acabamento);
    aPreparar.hidden = true;
    if (animar) {
      // primeiro a foto original na tela; depois a pincelada que a transforma no desenho "por pintar"
      estado.vista = 'numeros'; marcarVista();
      const telas = itemAntes.telas;
      estado.visor.textura(telas.foto);
      await new Promise((r) => setTimeout(r, 900));
      if (!ainda() || itemAntes.telas !== telas) return;
      await estado.visor.revelar(telas.foto, telas[estado.vista]);
      if (!ainda() || itemAntes.telas !== telas) return;
    } else {
      estado.visor.textura(itemAntes.telas[estado.vista]);
    }
    estado.visor.tintas(itemAntes.telas.paleta); // as tintas do kit pousadas no chão, no 3D
    mostrarPaleta();
    atualizarPainel();
  }
  function mostrarPaleta() {
    coresTitulo.hidden = false;
    coresTitulo.innerHTML = '';
    coresTitulo.append(document.createTextNode(`${T.cores} `));
    const n = el('span', 'th-alm__rotulo-nome'); n.textContent = `· ${formatar(T.coresAjuda, { n: estado.telas.paleta.length })}`;
    coresTitulo.append(n);
    paleta.replaceChildren(...estado.telas.paleta.map(({ n: num, cor }) => {
      const claro = 0.3 * cor[0] + 0.59 * cor[1] + 0.11 * cor[2] > 150;
      const pote = el('span', 'th-tela__pote'); pote.textContent = num;
      pote.style.background = `rgb(${cor.join(',')})`; pote.style.color = claro ? '#1b1f3a' : '#fff';
      return pote;
    }));
  }

  async function escolher(ficheiro) {
    if (!ficheiro) return;
    // foto recusada: se a tela já tinha foto, fica como estava e só aparece o aviso
    const falha = (msg) => { if (estado.ocupado) return; if (estado.imagem) { erroCarrinho.textContent = msg; erroCarrinho.hidden = false; } else faseVazio(msg); };
    if (estado.ocupado) return;
    if (!/^image\//.test(ficheiro.type)) { falha(T.erroTipo); return; }
    if (ficheiro.size > 15 * 1024 * 1024) { falha(formatar(T.erroTamanho, { mb: 15 })); return; }
    estado.aCarregar = true; atualizarPainel(); // enquanto a foto é lida, não se adiciona ao carrinho
    let img; try { img = await carregarImagem(ficheiro); } catch { estado.aCarregar = false; atualizarPainel(); falha(T.erroLer); return; }
    estado.aCarregar = false;
    if (estado.ocupado) return;
    erroCarrinho.hidden = true;
    // a foto vai para a tela escolhida (a 1.ª é a principal: as telas com "mesma foto" seguem-na)
    estado.atual = Math.min(alvoFoto, estado.itens.length - 1); alvoFoto = estado.atual;
    item().imagem = img; item().ficheiro = ficheiro; item().telas = null; item().noCarrinho = false;
    if (estado.atual === 0) estado.itens.forEach((it, i) => { if (i > 0 && !it.imagem) { it.telas = null; it.noCarrinho = false; } });
    sincronizar();
    desenharOpcoes();
    if (estado.visor) estado.visor.reiniciarRotacao();
    await processar(true);
  }
  entrada.addEventListener('change', () => { escolher(entrada.files?.[0]); entrada.value = ''; });
  // os outros sítios que abrem a escolha de foto (zona vazia, "mudar foto") mudam a tela que se está a ver
  const escolherAqui = () => { alvoFoto = estado.atual; entrada.click(); };

  /* carrinho */
  async function adicionar() {
    if (estado.ocupado || estado.aCarregar) return;
    if (!estado.itens[0].imagem) { alvoFoto = 0; entrada.click(); return; }
    // só as telas que ainda não estão no carrinho; todas têm de existir e estar disponíveis
    const lista = estado.itens.filter((it) => !it.noCarrinho);
    const ma = lista.find((it) => !varianteDe(it)?.disponivel);
    if (ma) { erroCarrinho.textContent = `${formatar(T.telaN, { n: estado.itens.indexOf(ma) + 1 })}: ${T.esgotado}`; erroCarrinho.hidden = false; return; }
    if (!lista.length) return;
    estado.ocupado = true;
    cta.disabled = true; cta.textContent = T.aAdicionar; erroCarrinho.hidden = true;
    const aVer = item();
    // volta a mostrar a tela que se estava a ver (também se falhar a meio); liberta as texturas das capturas
    const repor = () => {
      estado.atual = Math.max(0, estado.itens.indexOf(aVer));
      if (estado.itens.length > 1 && estado.telas && estado.visor) { estado.visor.limparTexturas(); estado.visor.montar(...dimensoes(), estado.acabamento); estado.visor.textura(estado.telas[estado.vista]); }
    };
    try {
      // uma linha do carrinho por tela: a foto (o servidor prepara o desenho) e a imagem da tela 3D
      const itens = [];
      for (const it of lista) {
        const vi = varianteDe(it), ki = vi.kit, img = it.imagem || estado.itens[0].imagem, fich = it.ficheiro || estado.itens[0].ficheiro;
        if (!it.telas) it.telas = converter(img, ki.menor, ki.maior);
        const dados = new FormData();
        dados.append('imagem', fich, fich.name || 'foto.jpg');
        try {
          const dims = it.telas.retrato === false ? [ki.maior, ki.menor] : [ki.menor, ki.maior];
          if (estado.itens.length > 1) estado.visor.montar(...dims, it.acabamento);
          const blob = await estado.visor.capturaBlob(it.telas.pintada, it.telas[estado.vista]); if (blob) dados.append('preview', blob, 'tela.png');
          // não acumula texturas na placa gráfica (e volta logo a pôr uma, para a que estava aplicada não ficar perdida)
          if (estado.itens.length > 1) { estado.visor.limparTexturas(); estado.visor.textura(it.telas[estado.vista]); }
        } catch { /* sem imagem no carrinho */ }
        const r = await fetch(`${String(cfg.servidor).replace(/\/$/, '')}/tela/foto`, { method: 'POST', body: dados });
        const json = await r.json().catch(() => ({}));
        if (!r.ok || !json.foto_url) throw new Error(T.erros[json.codigo] || T.erroCarrinho);
        const propriedades = { _tela_foto: json.foto_url };
        if (json.preview_url) propriedades._preview = json.preview_url;
        itens.push({ id: vi.id, quantity: 1, properties: propriedades });
      }
      repor();
      const gaveta = document.querySelector('cart-drawer');
      const corpo = { items: itens };
      if (gaveta?.getSectionsToRender) {
        corpo.sections = gaveta.getSectionsToRender().map((s) => s.id);
        corpo.sections_url = window.location.pathname;
      }
      const rc = await fetch(`${raizLoja}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(corpo),
      });
      const resposta = await rc.json();
      if (!rc.ok) throw new Error(resposta.description || resposta.message || T.erroCarrinho);
      if (gaveta?.getSectionsToRender && resposta.sections) {
        // fica na página: atualiza o ícone do carrinho e a gaveta sem a abrir (como o renderContents do Dawn)
        try {
          gaveta.classList.remove('is-empty');
          for (const s of gaveta.getSectionsToRender()) {
            const alvo = s.selector ? document.querySelector(s.selector) : document.getElementById(s.section || s.id);
            const html = resposta.sections[s.id];
            if (!alvo || !html) continue;
            const doc = new DOMParser().parseFromString(html, 'text/html');
            alvo.innerHTML = (doc.querySelector(s.selector || '.shopify-section') || doc.body).innerHTML;
          }
        } catch (e) { console.warn('[tela] não foi possível atualizar o carrinho', e); }
      }
      document.dispatchEvent(new CustomEvent('th:tela:adicionada', { detail: resposta }));
      lista.forEach((it) => { it.noCarrinho = true; }); sincronizar();
      estado.ocupado = false;
      desenharTelas(); atualizarPainel();
    } catch (e) {
      repor();
      estado.ocupado = false;
      erroCarrinho.textContent = e.message || T.erroCarrinho;
      erroCarrinho.hidden = false;
      atualizarPainel();
    }
  }
  cta.addEventListener('click', adicionar);
  btnOutra.addEventListener('click', () => {
    if (estado.ocupado) return;
    estado.adicionado = false; estado.itens = [novaTela(item())]; estado.atual = 0; alvoFoto = 0;
    desenharOpcoes(); faseVazio();
    raiz.scrollIntoView({ behavior: 'smooth', block: 'start' });
    entrada.click();
  });

  desenharOpcoes();
  faseVazio();
  const api = { destruir() { estado.visor?.destruir(); raiz._thTela = null; }, estado };
  raiz._thTela = api;
  return api;
}

export function iniciarTodos(contexto = document) {
  contexto.querySelectorAll('[data-th-tela]').forEach((raiz) => {
    if (raiz._thTela) return;
    const json = raiz.querySelector('script[type="application/json"]');
    if (!json) return;
    try { iniciar(raiz, JSON.parse(json.textContent)); } catch (e) { console.error('[tela] configuração inválida', e); }
  });
}

if (!window.__thTelaManual) {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => iniciarTodos());
  else iniciarTodos();
  document.addEventListener('shopify:section:load', (e) => iniciarTodos(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-th-tela]').forEach((r) => r._thTela?.destruir());
  });
}
